import { yupResolver } from "@hookform/resolvers/yup";
import {
	Alert,
	Button,
	designTokens,
	FormControl,
	FormControlText,
	FormGroup,
	Input,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Modalv2
} from "@itandi/itandi-bb-ui";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import type { CompanyVendor } from "~/models/CompanyVendor";
import type { CompanyVendorPayloadCreateParam } from "~/models/CompanyVendorPayload";
import { validationSchemaPayload } from "~/models/CompanyVendorPayload";
import { isSuccess } from "~/utils/api/api_handler";
import { create as createPayload } from "~/utils/api/CompanyVendorPayload";
import { generatePath } from "~/utils/generatePath";

type Props = {
	companyVendor: CompanyVendor;
	onClose: () => void;
};

export function InviteCompanyVendorModal({ onClose, companyVendor }: Props) {
	const formId = useId();
	const { mutate: globalMutate } = useSWRConfig();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isDirty }
	} = useForm<CompanyVendorPayloadCreateParam>({
		defaultValues: {
			companyVendorId: companyVendor.id,
			email: ""
		},
		mode: "onSubmit",
		resolver: yupResolver(validationSchemaPayload)
	});

	const handleSave = async (submitValue: CompanyVendorPayloadCreateParam) => {
		const result = await createPayload(submitValue, companyVendor.id);
		if (isSuccess(result)) {
			await globalMutate(generatePath("/company_vendors"));
			onClose();
			toast.success("招待メールを送信しました。");
		} else {
			toast.error(`エラーが発生しました。${result.message ?? ""}`);
		}
	};

	return (
		<Modalv2 isOpen onClose={onClose}>
			<ModalHeader title="施工会社を招待" />
			<ModalBody>
				<Alert mb={designTokens["spacing/m"]} variant="info">
					連携すると、報告画像の共有や見積書の承認、チャットなどをシステム上で行えます。
				</Alert>
				<form id={formId} onSubmit={handleSubmit(handleSave)}>
					<FormGroup variant="vertical">
						<FormControl
							isInvalid={!!errors.email}
							label="招待先の代表者メールアドレス"
						>
							<Input {...register("email")} />
							<FormControlText
								error={errors.email?.message}
								help={
									"招待メールが送信され、施工会社様がアカウントを作成することで連携が完了します"
								}
							/>
						</FormControl>
					</FormGroup>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button disabled={isSubmitting} onClick={onClose} variant="secondary">
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={!isDirty}
						form={formId}
						loading={isSubmitting}
						type="submit"
						variant="primary"
					>
						招待
					</Button>
				}
			/>
		</Modalv2>
	);
}
